import { create } from "zustand";
import { httpClient } from "@/services/api/httpClient";
import type { DataI18nMap } from "@/lib/i18n/dataText";

/**
 * Catálogo de tradução do CONTEÚDO vindo do cadastro (título de card, rótulo de
 * estado, categoria de documento). O texto da interface fica no i18next; isto é
 * o que o backend publica em `max.maxiot.i18n_catalogo.get`.
 *
 * Estratégia igual à do siteConfigStore: hidrata do localStorage na hora (a tela
 * abre com os títulos já traduzidos) e revalida em segundo plano. A `version`
 * — hash do payload calculado no servidor — é o que evita ficar preso num texto
 * antigo: se ela mudou, o cache é substituído.
 *
 * Falhar em carregar não é erro fatal: sem catálogo o resolver devolve o texto
 * original, que é exatamente o comportamento de antes.
 */

const CACHE_KEY = "data_i18n_catalog";

interface CatalogoPersistido {
  version: string;
  messages: DataI18nMap;
}

interface DataI18nState {
  messages: DataI18nMap;
  version: string | null;
  isReady: boolean;

  init: () => Promise<void>;
  reload: () => Promise<void>;
}

function lerCache(): CatalogoPersistido | null {
  try {
    const bruto = localStorage.getItem(CACHE_KEY);
    if (!bruto) return null;
    const dados = JSON.parse(bruto) as CatalogoPersistido;
    if (!dados || typeof dados.messages !== "object") return null;
    return dados;
  } catch (e) {
    console.warn("[dataI18nStore] cache inválido, descartando", e);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function gravarCache(dados: CatalogoPersistido) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(dados));
  } catch (e) {
    console.warn("[dataI18nStore] não foi possível gravar o cache", e);
  }
}

export const useDataI18nStore = create<DataI18nState>((set, get) => ({
  messages: {},
  version: null,
  isReady: false,

  init: async () => {
    const cache = lerCache();
    if (cache) set({ messages: cache.messages, version: cache.version });
    await get().reload();
  },

  reload: async () => {
    try {
      const resposta = await httpClient.get<{
        message?: { version?: string; messages?: DataI18nMap };
      }>(
        "/api/method/max.maxiot.i18n_catalogo.get",
        // skipCache: reload() existe justamente para conferir se o servidor tem
        // versão nova — com o cache do httpClient em cima, ele devolveria a
        // resposta anterior e a revalidação não revalidaria nada.
        { skipCache: true },
      );

      // O Frappe embrulha o retorno de /api/method em `message`; alguns caminhos
      // do httpClient já desembrulham, então aceitamos as duas formas.
      const dados = (resposta as any)?.message ?? resposta;
      const messages = (dados?.messages ?? {}) as DataI18nMap;
      const version = (dados?.version ?? null) as string | null;

      if (version && version === get().version) {
        set({ isReady: true });
        return;
      }

      set({ messages, version, isReady: true });
      if (version) gravarCache({ version, messages });
    } catch (err) {
      // Sem catálogo o app mostra o texto original — degradação silenciosa, de
      // propósito: isto não pode impedir a tela de abrir.
      console.warn("[dataI18nStore] falha ao carregar o catálogo de conteúdo", err);
      set({ isReady: true });
    }
  },
}));

export const selectDataMessages = (s: DataI18nState) => s.messages;
